import PostType, { CampaignType } from '@/constants/postType.constants';
import useUserProfile from '@/hooks/useUserProfile';
import { uploadToCloudinary } from '@/utils/cloudinary';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { endOfDay, isAfter } from 'date-fns';
import produce from 'immer';
import {
  set as _set,
  cloneDeep,
  get,
  isArray,
  isEmpty,
  last,
  size,
} from 'lodash-es';
import { useMemo } from 'react';
import { create } from 'zustand';
import { fetchViaProxy } from './proxy-fetch';
import { authorizedServerFetch } from './server.actions';
import { ProfileTypes } from '@/constants/profileType.constant';

export const feedCacheKey = ['active-feed'];

/**************** PRIVATE FUNCTIONS AND FETECHERS ******************** */

const preProcessCelebration = async (payload, files) => {
  try {
    if (files && Array.isArray(files) && files.length > 0) {
      if (files[0].type) {
        const type = files[0].type.includes('image') ? 'image' : 'video';
        const uploadedFiles = await Promise.all(uploadToCloudinary(files));
        payload.attachmentUrl = uploadedFiles[0];
        payload.attachmentType = type;
      }
    }

    const formData = new FormData();
    formData.append('data', JSON.stringify(payload));
    return formData;
  } catch (error) {
    console.log(error);
    return null;
  }
};

const preProcessVolunteeringOpportunity = async (
  formValues,
  post,
  images,
  videos,
) => {
  const isNew = isEmpty(post);

  const {
    causes: selectedCauses,
    mandatorySkills,
    optionalSkills,
    imageUrl,
    ...payload
  } = formValues;

  payload.mandatorySkills = mandatorySkills.map((skill) => skill.id);
  payload.optionalSkills = optionalSkills.map((skill) => skill.id);
  payload.causes = selectedCauses.map((cause) => cause.id);

  const formData = new FormData();
  formData.append('data', JSON.stringify(payload));

  if (images && isArray(images) && size(images) > 0) {
    images.forEach((image) => {
      if (image instanceof File) {
        formData.append('image', image);
      }
    });
  }

  if (videos && isArray(videos) && size(videos) > 0) {
    let uploadedVideos = [];
    if (videos[0].type) {
      try {
        uploadedVideos = await Promise.all(uploadToCloudinary(videos));
        formData.append('attachmentVideos', JSON.stringify(uploadedVideos));
      } catch (error) {
        console.log(error);
        return null;
      }
    } else {
      uploadedVideos = videos.map((video) => video.url);
      formData.append('attachmentVideos', JSON.stringify(uploadedVideos));
    }
  }

  return formData;
};

const preProcessProject = async (formValues, post, images, videos) => {
  const { ...payload } = formValues;

  const formData = new FormData();
  formData.append('data', JSON.stringify(payload));

  if (images && isArray(images) && size(images) > 0) {
    images.forEach((image) => {
      if (image instanceof File) {
        formData.append('imageUrl', image);
      }
    });
  }

  if (videos && isArray(videos) && size(videos) > 0) {
    let uploadedVideos = [];
    if (videos[0].type) {
      try {
        uploadedVideos = await Promise.all(uploadToCloudinary(videos));
        formData.append('attachmentVideos', JSON.stringify(uploadedVideos));
      } catch (error) {
        console.log(error);
        return null;
      }
    } else {
      uploadedVideos = videos.map((video) => video.url);
      formData.append('attachmentVideos', JSON.stringify(uploadedVideos));
    }
  }

  return formData;
};

const preProcessCampaign = async (data, images, videos, post) => {
  const payload = {
    postAs: data.postAs,
    targetViewers: data.targetViewers,
    targetOrgs: data.targetOrgs,
    nonProfitId: data.nonProfit.id,
    projectId: data.project?.id || null,
    title: data.title,
    message: data.description,
    startDate: data.startDate,
    endDate: data.endDate,
    address: data.address,
    videoUrl: data.videoUrl,
    campaignType: data.campaignType,
    attachmentType: 'image',
    isFeatured: data.isFeatured,
    nekiAdminAuthor: data.nekiAdminAuthor || null,
    nekiAdminOrg: data.nekiAdminOrg || null,
  };

  if (data.campaignType === CampaignType.FundRaise) {
    payload.goalAmount = data.goalAmount;
    payload.fundRaiseType = data.fundRaiseType;
  }

  const formData = new FormData();
  formData.append('data', JSON.stringify(payload));

  if (images && isArray(images) && !isEmpty(images)) {
    images.forEach((image) => {
      if (image instanceof File) {
        formData.append('attachment', image);
      }
    });
  }

  if (videos && isArray(videos) && !isEmpty(videos)) {
    let uploadedVideos = [];
    try {
      if (videos[0].type) {
        uploadedVideos = await Promise.all(uploadToCloudinary(videos));
      } else {
        uploadedVideos = videos.map((video) => video.url);
      }
      formData.append('attachmentVideos', JSON.stringify(uploadedVideos));
    } catch (error) {
      console.log('Error uploading videos', error);
      return null;
    }
  }

  return formData;
};

const preProcessCrowdFund = async (data, images, videos, post) => {
  const payload = {
    postAs: data.postAs,
    targetViewers: data.targetViewers,
    targetOrgs: data.targetOrgs,
    fundraisingOrgId: data.fundraisingOrg.id,
    title: data.title,
    message: data.description,
    startDate: data.startDate,
    endDate: data.endDate,
    videoUrl: data.videoUrl,
    attachmentType: 'image',
    isFeatured: data.isFeatured,
    fundraisingOrg: data.fundraisingOrg,
    contributionUrl: data.contributionUrl,
    beneficiary: data.beneficiary,
    currency: data.currency,
    goalAmount: data.goalAmount,
  };

  const formData = new FormData();
  formData.append('data', JSON.stringify(payload));

  if (images && isArray(images) && !isEmpty(images)) {
    images.forEach((image) => {
      if (image instanceof File) {
        formData.append('attachment', image);
      }
    });
  }

  if (videos && isArray(videos) && !isEmpty(videos)) {
    let uploadedVideos = [];
    try {
      if (videos[0].type) {
        uploadedVideos = await Promise.all(uploadToCloudinary(videos));
      } else {
        uploadedVideos = videos.map((video) => video.url);
      }
      formData.append('attachmentVideos', JSON.stringify(uploadedVideos));
    } catch (error) {
      console.log('Error uploading videos', error);
      return null;
    }
  }

  return formData;
};

const fetchPosts = async (postType, before, filters) => {
  const params = new URLSearchParams({
    limit: 10,
    type: postType,
  });
  if (before) {
    params.append('before', before);
  }

  let subTypes = {};

  switch (postType) {
    case PostType.Campaign:
      subTypes = filters.donate;
      break;
    case PostType.Volunteering:
      subTypes = filters.volunteer;
      break;
    default:
      break;
  }

  // Convert subTypes object to a comma-separated string
  const subTypesString = Object.entries(subTypes)
    .map(([key, value]) => key)
    .join(',');

  if (subTypesString) {
    params.append('subTypes', subTypesString);
  }

  const { ok, data } = await fetchViaProxy(
    `/proxy/v2/protected/users/me/feed?${params.toString()}`,
    { method: 'GET' },
  );

  if (!ok) {
    throw new Error(`Error fetching posts`);
  }

  return {
    posts: data.feed,
    hasMore: size(data.feed) > 0,
  };
};

const getPostFromCache = (postId, queryClient) => {
  const cache = queryClient.getQueryData(feedCacheKey);

  // Check if cache and cache.pages are defined
  if (!cache || !Array.isArray(cache.pages)) {
    return {
      pageIndex: -1,
      postIndex: -1,
      post: null,
    };
  }

  const pageIndex = cache.pages.findIndex((page) => {
    return page.posts.some((post) => post.id === postId);
  });

  if (pageIndex === -1) {
    return {
      pageIndex: -1,
      postIndex: -1,
      post: null,
    };
  }

  const postIndex = cache.pages[pageIndex].posts.findIndex(
    (post) => post.id === postId,
  );

  if (postIndex === -1) {
    return {
      pageIndex: -1,
      postIndex: -1,
      post: null,
    };
  }

  return {
    pageIndex,
    postIndex,
    post: cache.pages[pageIndex].posts[postIndex],
  };
};

/*************** HOOKS AND PUBLIC FUNCTIONS ********************** */

export const replacePostInCache = (post, queryClient) => {
  const { pageIndex, postIndex } = getPostFromCache(post.id, queryClient);
  queryClient.setQueryData(feedCacheKey, (oldData) => {
    return produce(oldData, (draft) => {
      draft.pages[pageIndex].posts[postIndex] = post;
    });
  });
};

export const useResetFeedCache = (queryKey = feedCacheKey) => {
  const queryClient = useQueryClient();
  return useMemo(() => {
    return () => {
      console.log('resetting feed cache');
      queryClient.cancelQueries({ queryKey: queryKey });
      queryClient.removeQueries({ queryKey: queryKey });
      // queryClient.refetchQueries({ queryKey: feedCacheKey });
    };
  }, [queryClient]);
};

export const useHomeFeedFilters = create((set) => {
  return {
    postType: 'all',
    counts: {},
    filters: {
      donate: {
        fundraisers: false,
        inKind: false,
      },
      volunteer: {
        inPerson: false,
        virtual: false,
        volunteer: false,
      },
      nonProfitNews: false,
      others: {
        nonProfitNews: false,
      },
    },
    setPostType: (type) => {
      set(
        produce((draft) => {
          console.log('setting post type', type);
          draft.postType = type;
        }),
      );
    },
    setPostTypeFilters: (postTypeFilters) => {
      set(
        produce((draft) => {
          draft.filters = postTypeFilters;
        }),
      );
    },
    setDonateFilters: (donateFilters) => {
      set(
        produce((draft) => {
          draft.filters.donate = donateFilters;
        }),
      );
    },
    setVolunteerFilters: (volunteerFilters) => {
      set(
        produce((draft) => {
          draft.filters.volunteer = volunteerFilters;
        }),
      );
    },
    setNonProfitNewsFilters: (nonProfitNews) => {
      set(
        produce((draft) => {
          draft.filters.nonProfitNews = nonProfitNews;
        }),
      );
    },
    setOtherFilters: (otherFilters) => {
      set(
        produce((draft) => {
          draft.filters.others = otherFilters;
        }),
      );
    },
    resetFilters: () => {
      set(
        produce((draft) => {
          draft.postType = 'all';
          draft.filters = {
            donate: {
              fundraisers: false,
              inKind: false,
            },
            volunteer: {
              inPerson: false,
              virtual: false,
            },
            nonProfitNews: false,
            others: {
              nonProfitNews: false,
            },
          };
        }),
      );
    },
    setCounts: (counts) => {
      set(
        produce((draft) => {
          draft.counts = counts;
        }),
      );
    },
  };
});

// export const useFetchUserFeed = (postType, initialData, filters) => {
//   return useInfiniteQuery({
//     queryKey: feedCacheKey,
//     queryFn: ({ pageParam }) => fetchPosts(postType, pageParam, filters),
//     getNextPageParam: (lastPage) => {
//       if (lastPage && !lastPage.hasMore) {
//         return undefined;
//       }

//       const lastPostDate = last(lastPage.posts)?.updatedOn;
//       return lastPostDate || new Date().toISOString();
//     },
//     initialData: () => {

//       console.log('initialData', initialData);
//       if (size(initialData) === 0) {
//         return undefined;
//       }

//       return {
//         pages: [
//           {
//             posts: initialData,
//             hasMore: size(initialData) > 0,
//           },
//         ],
//       };
//     },
//     staleTime: Infinity,
//     cacheTime: Infinity,
//   });
// };

export const useFetchUserFeed = (postType, initialData, filters) => {
  const hasInitialData = size(initialData) > 0;

  return useInfiniteQuery({
    queryKey: feedCacheKey,
    queryFn: ({ pageParam }) => fetchPosts(postType, pageParam, filters),
    getNextPageParam: (lastPage) => {
      if (lastPage && !lastPage.hasMore) {
        return undefined;
      }

      const lastPostDate = last(lastPage.posts)?.updatedOn;
      return lastPostDate || new Date().toISOString();
    },
    initialData: hasInitialData
      ? {
          pages: [
            {
              posts: initialData,
              hasMore: size(initialData) > 0,
            },
          ],
          pageParams: [undefined],
        }
      : undefined,
    enabled: !hasInitialData, // Disable query if initialData is present
    staleTime: Infinity,
    cacheTime: Infinity,
  });
};

export const useGetFirstPost = (post) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: feedCacheKey,
    queryFn: () => {
      const cache = queryClient.getQueryData(feedCacheKey);
      const cached = get(cache, 'pages[0].posts[0]', null);
      if (!cached || cached.id !== post.id) {
        queryClient.setQueryData(feedCacheKey, (oldData) => {
          return produce(oldData, (draft) => {
            draft.pages = [
              {
                posts: [post],
                hasMore: false,
              },
            ];
          });
        });
      }
      return queryClient.getQueryData(feedCacheKey);
    },
    initialData: () => {
      return {
        pages: [
          {
            posts: [post],
            hasMore: false,
          },
        ],
      };
    },
    refetchOnMount: true,
    staleTime: Infinity,
    cacheTime: Infinity,
  });
};

export const useUpdatePostReaction = () => {
  const userProfile = useUserProfile();
  const selectedProfile = userProfile?.selectedProfile;
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: 'update-post-reaction',
    mutationFn: async ({ postId, reaction }) => {
      const pathPart = reaction ? 'like' : 'unlike';
      const path = `/proxy/v1/posts/${postId}/${pathPart}`;

      console.log(
        'useUpdatePostReaction postId reaction',
        postId,
        reaction,
        path,
      );

      const { data, ok } = await fetchViaProxy(path, {
        method: 'POST',
        body: JSON.stringify({
          as: selectedProfile,
          likeType: reaction,
        }),
      });

      console.log('useUpdatePostReaction like', data);

      if (!ok) {
        throw new Error(`Error reacting to post ${postId}`);
      }
      return data;
    },
    onMutate: ({ postId, reaction }) => {
      const hasReaction = !isEmpty(reaction);
      const { post, pageIndex, postIndex } = getPostFromCache(
        postId,
        queryClient,
      );
      console.log('onMutate', post, pageIndex, postIndex);
      queryClient.setQueryData(feedCacheKey, (oldData) => {
        return produce(oldData, (draft) => {
          const hadReaction = post.userReaction.hasReaction;
          draft.pages[pageIndex].posts[postIndex].userReaction = {
            ...draft.pages[pageIndex].posts[postIndex].userReaction,
            hasReaction: hasReaction,
            reactionType: reaction,
            as: hasReaction ? selectedProfile : null,
          };

          if (!hadReaction && hasReaction) {
            draft.pages[pageIndex].posts[postIndex].totalReactions += 1;
          } else if (hadReaction && !hasReaction) {
            draft.pages[pageIndex].posts[postIndex].totalReactions -= 1;
          }
        });
      });

      return {
        cached: post,
      };
    },
    onSuccess: (serverData, args) => {
      const { postId } = args;
      // TODO: Shyam - handle reward
      const { userReaction, totalReactions, reactions, reward } = serverData;
      const { post, pageIndex, postIndex } = getPostFromCache(
        postId,
        queryClient,
      );

      queryClient.setQueryData(feedCacheKey, (oldData) => {
        return produce(oldData, (draft) => {
          const updated = {
            ...post,
            reactions: reactions,
            totalReactions: totalReactions,
            userReaction: userReaction,
          };

          draft.pages[pageIndex].posts[postIndex] = updated;
        });
      });
    },
    onError: (err, args, context) => {
      const { postId } = args;
      const { cached } = context;
      const { pageIndex, postIndex } = getPostFromCache(postId, queryClient);

      queryClient.setQueryData(feedCacheKey, (oldData) => {
        return produce(oldData, (draft) => {
          draft.pages[pageIndex].posts[postIndex] = cached;
        });
      });
    },
  });
};

export const useAddOrUpdatePost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: 'add-post',
    mutationFn: async ({ formData, postId }) => {
      const isEdit = !!postId;
      const options = {
        method: isEdit ? 'PUT' : 'POST',
        body: formData,
      };

      const basePath = 'userposts';
      const path = isEdit ? `${basePath}/${postId}` : basePath;

      const { ok, data } = await authorizedServerFetch(path, options);

      if (!ok) {
        throw new Error(`Error creating post`);
      }

      return data;
    },
    onSuccess: (serverData, { postId }) => {
      const isEdit = !!postId;
      // TODO - Shyam handle reward
      const { post, reward } = serverData;

      if (isEdit) {
        const { pageIndex, postIndex } = getPostFromCache(postId, queryClient);
        queryClient.setQueryData(feedCacheKey, (oldData) => {
          return produce(oldData, (draft) => {
            draft.pages[pageIndex].posts[postIndex] = post;
          });
        });
      } else {
        queryClient.setQueryData(feedCacheKey, (oldData) => {
          return produce(oldData, (draft) => {
            draft.pages[0].posts = [post, ...draft.pages[0].posts];
          });
        });
        if (post.isFeatured) {
          queryClient.setQueryData(['home-feed-featured-posts'], (oldData) => {
            return produce(oldData, (draft) => {
              draft.unshift(post);
            });
          });
        }
      }
    },
  });
};

export const useDeletePost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: 'delete-post',
    mutationFn: async ({ postId }) => {
      const { ok } = await authorizedServerFetch(`posts/${postId}`, {
        method: 'DELETE',
      });

      if (!ok) {
        throw new Error(`Error deleting post ${postId}`);
      }

      return postId;
    },
    onMutate: ({ postId }) => {
      const { post, pageIndex, postIndex } = getPostFromCache(
        postId,
        queryClient,
      );
      queryClient.setQueryData(feedCacheKey, (oldData) => {
        return produce(oldData, (draft) => {
          draft.pages[pageIndex].posts.splice(postIndex, 1);
        });
      });

      if (post.isFeatured) {
        queryClient.setQueryData(['home-feed-featured-posts'], (oldData) => {
          return produce(oldData, (draft) => {
            draft.splice(
              draft.findIndex((p) => p.id === postId),
              1,
            );
          });
        });
      }

      return {
        cached: post,
        pageIndex,
        postIndex,
      };
    },
    onError: (err, args, context) => {
      const { cached, pageIndex, postIndex } = context;

      queryClient.setQueryData(feedCacheKey, (oldData) => {
        return produce(oldData, (draft) => {
          draft.pages[pageIndex].posts.splice(postIndex, 0, cached);
        });
      });
    },
  });
};

export const useReportPost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: 'report-post',
    mutationFn: async ({ postId }) => {
      const { ok } = await authorizedServerFetch(`posts/${postId}/report`, {
        method: 'POST',
      });

      if (!ok) {
        throw new Error(`Error reporting post ${postId}`);
      }

      return postId;
    },
    onMutate: ({ postId }) => {
      const { post, pageIndex, postIndex } = getPostFromCache(
        postId,
        queryClient,
      );
      queryClient.setQueryData(feedCacheKey, (oldData) => {
        return produce(oldData, (draft) => {
          draft.pages[pageIndex].posts.splice(postIndex, 1);
        });
      });

      return {
        cached: post,
        pageIndex,
        postIndex,
      };
    },
    onError: (err, args, context) => {
      const { cached, pageIndex, postIndex } = context;

      queryClient.setQueryData(feedCacheKey, (oldData) => {
        return produce(oldData, (draft) => {
          draft.pages[pageIndex].posts.splice(postIndex, 0, cached);
        });
      });
    },
  });
};

export const useTogglePostPublic = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: 'toggle-post-public',
    mutationFn: async ({ postId, isPublic }) => {
      const { ok, data } = await authorizedServerFetch(
        `posts/${postId}/public`,
        {
          method: 'POST',
          body: JSON.stringify({
            isPublic,
          }),
        },
      );

      if (!ok) {
        throw new Error(`Error toggling post ${postId}`);
      }

      return data.post;
    },
    onSuccess: (postFromServer, { postId }) => {
      const { pageIndex, postIndex } = getPostFromCache(postId, queryClient);
      queryClient.setQueryData(feedCacheKey, (oldData) => {
        return produce(oldData, (draft) => {
          const props = [
            'publicShares',
            'targetViewers',
            'targetOrgs',
            'isFeatured',
          ];
          props.forEach((prop) => {
            draft.pages[pageIndex].posts[postIndex][prop] =
              postFromServer[prop];
          });
        });
      });
    },
  });
};

export const useTogglePostFeatured = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: 'toggle-featured-posts',
    mutationFn: async ({ postId, isFeatured }) => {
      const { ok, data } = await fetchViaProxy(
        `/proxy/v2/protected/posts/${postId}/feature`,
        {
          method: 'POST',
          body: JSON.stringify({
            featured: isFeatured,
          }),
        },
      );

      if (!ok) {
        throw new Error(`Error toggling post ${postId}`);
      }

      return data.post;
    },
    onSuccess: (postFromServer, { postId, isFeatured }) => {
      const { pageIndex, postIndex, post } = getPostFromCache(
        postId,
        queryClient,
      );
      queryClient.setQueryData(feedCacheKey, (oldData) => {
        return produce(oldData, (draft) => {
          const props = ['isFeatured', 'publicShares'];
          props.forEach((prop) => {
            draft.pages[pageIndex].posts[postIndex][prop] =
              postFromServer[prop];
          });
        });
      });

      queryClient.setQueryData(['home-feed-featured-posts'], (oldData) => {
        return produce(oldData, (draft) => {
          if (isFeatured && post) {
            const props = ['isFeatured', 'publicShares'];
            const featuredPost = cloneDeep(post);
            props.forEach((prop) => {
              featuredPost[prop] = postFromServer[prop];
            });

            draft.unshift(featuredPost);
          } else if (!isFeatured) {
            const index = draft.findIndex((p) => p.id === postId);
            if (index >= 0) {
              draft.splice(index, 1);
            }
          }
        });
      });
    },
  });
};

export const useToggleNewsHomeFeedVisibility = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: 'toggle-news-home-feed-visibility',
    mutationFn: async ({ postId, isVisibleOnHomeFeed }) => {
      const { ok, data } = await authorizedServerFetch(
        `posts/${postId}/home-feed-visibility`,
        {
          method: 'POST',
          body: JSON.stringify({
            isVisibleOnHomeFeed,
          }),
        },
      );

      if (!ok) {
        throw new Error(`Error toggling news post home feed ${postId}`);
      }

      return data.post;
    },
    onSuccess: (postFromServer, { postId }) => {
      // TODO:
      const { pageIndex, postIndex } = getPostFromCache(postId, queryClient);
      queryClient.setQueryData(feedCacheKey, (oldData) => {
        return produce(oldData, (draft) => {
          const props = ['homeFeedVisibility'];
          props.forEach((prop) => {
            draft.pages[pageIndex].posts[postIndex][prop] =
              postFromServer[prop];
          });
        });
      });
    },
  });
};
export const useUpdatePostInCache = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: 'update-post-in-cache',
    mutationFn: async ({ postId, props }) => {
      const { pageIndex, postIndex } = getPostFromCache(postId, queryClient);
      if (pageIndex >= 0 && postIndex >= 0) {
        queryClient.setQueryData(feedCacheKey, (oldData) => {
          return produce(oldData, (draft) => {
            props.forEach(({ key, value }) => {
              _set(draft.pages[pageIndex].posts[postIndex], key, value);
            });
          });
        });
      }
    },
  });
};

export const useAddOrUpdateCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['add-campaign'],
    mutationFn: async ({ data, post, images, videos }) => {
      const isEdit = !!post;

      const basePath = 'campaigns';
      const path = isEdit ? `${basePath}/${post.sourcePostData.id}` : basePath;

      const formData = await preProcessCampaign(data, images, videos, post);

      if (!formData) {
        throw new Error(`Error creating campaign`);
      }

      const options = {
        method: isEdit ? 'PUT' : 'POST',
        body: formData,
      };

      const { ok, data: serverData } = await authorizedServerFetch(
        path,
        options,
      );

      if (!ok || !serverData) {
        throw new Error(`Error creating post`);
      }

      return serverData.data;
    },
    onSuccess: (serverData, { post }) => {
      const isEdit = !!post;
      const { post: campaignPost } = serverData;
      if (isEdit) {
        const { pageIndex, postIndex } = getPostFromCache(post.id, queryClient);
        if (pageIndex >= 0 && postIndex >= 0) {
          queryClient.setQueryData(feedCacheKey, (oldData) => {
            return produce(oldData, (draft) => {
              draft.pages[pageIndex].posts[postIndex] = campaignPost;
            });
          });
        }
      } else {
        const startDate = new Date(campaignPost.sourcePostData.startDate);
        if (!isAfter(startDate, endOfDay(new Date()))) {
          queryClient.setQueryData(feedCacheKey, (oldData) => {
            return produce(oldData, (draft) => {
              draft.pages[0].posts = [campaignPost, ...draft.pages[0].posts];
            });
          });

          if (campaignPost.isFeatured) {
            queryClient.setQueryData(
              ['home-feed-featured-posts'],
              (oldData) => {
                return produce(oldData, (draft) => {
                  draft.unshift(campaignPost);
                });
              },
            );
          }
        }
      }
    },
  });
};

export const useAddOrUpdateVolunteering = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['add-update-volunteering'],
    mutationFn: async ({ formValues, post, images, videos }) => {
      const isEdit = !!post;

      const basePath = 'admin/nonprofit/volunteerings';
      const path = isEdit
        ? `${basePath}/${
            post.sourcePostData?.volunteeringOpportunity?.id ||
            post.sourcePostData.volunteerOpportunity.id
          }`
        : basePath;

      const formData = await preProcessVolunteeringOpportunity(
        formValues,
        post,
        images,
        videos,
      );

      if (!formData) {
        throw new Error(`Error creating volunteering opportunity`);
      }

      const options = {
        method: isEdit ? 'PUT' : 'POST',
        body: formData,
      };

      const { ok, data: serverData } = await authorizedServerFetch(
        path,
        options,
      );

      if (!ok || !serverData) {
        throw new Error(`Error creating post`);
      }

      return serverData.data;
    },
    onSuccess: (serverData, { post }) => {
      const isEdit = !!post;
      const { post: volunteeringPost } = serverData;
      if (isEdit) {
        const { pageIndex, postIndex } = getPostFromCache(post.id, queryClient);
        if (pageIndex >= 0 && postIndex >= 0) {
          queryClient.setQueryData(feedCacheKey, (oldData) => {
            return produce(oldData, (draft) => {
              draft.pages[pageIndex].posts[postIndex] = volunteeringPost;
            });
          });
        }
      } else {
        queryClient.setQueryData(feedCacheKey, (oldData) => {
          return produce(oldData, (draft) => {
            draft.pages[0].posts = [volunteeringPost, ...draft.pages[0].posts];
          });
        });

        if (volunteeringPost.isFeatured) {
          queryClient.setQueryData(['home-feed-featured-posts'], (oldData) => {
            return produce(oldData, (draft) => {
              draft.unshift(volunteeringPost);
            });
          });
        }
      }
    },
  });
};

export const useAddOrUpdateProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['add-update-project'],
    mutationFn: async ({ formValues, post, images, videos }) => {
      const isEdit = !!post;

      const basePath = 'admin/nonprofit/projects';
      const path = isEdit ? `${basePath}/${post.sourcePostData.id}` : basePath;

      const formData = await preProcessProject(
        formValues,
        post,
        images,
        videos,
      );

      if (!formData) {
        throw new Error(`Error creating project`);
      }

      const options = {
        method: isEdit ? 'PUT' : 'POST',
        body: formData,
      };

      const { ok, data: serverData } = await authorizedServerFetch(
        path,
        options,
      );

      if (!ok || !serverData) {
        throw new Error(`Error creating post`);
      }

      return serverData.data;
    },
    onSuccess: (serverData, { post }) => {
      const isEdit = !!post;
      const { post: projectPost } = serverData;
      if (isEdit) {
        const { pageIndex, postIndex } = getPostFromCache(post.id, queryClient);
        if (pageIndex >= 0 && postIndex >= 0) {
          queryClient.setQueryData(feedCacheKey, (oldData) => {
            return produce(oldData, (draft) => {
              draft.pages[pageIndex].posts[postIndex] = projectPost;
            });
          });
        }
      } else {
        queryClient.setQueryData(feedCacheKey, (oldData) => {
          return produce(oldData, (draft) => {
            // TODO: check
            // draft.pages[0].posts = [projectPost, ...draft.pages[0].posts];
            if (draft) {
              draft.pages[0].posts = [projectPost, ...draft.pages[0].posts];
            }
          });
        });

        if (projectPost.isFeatured) {
          queryClient.setQueryData(['home-feed-featured-posts'], (oldData) => {
            return produce(oldData, (draft) => {
              // TODO: check
              // draft.unshift(projectPost);
              if (draft) {
                draft.unshift(projectPost);
              }
            });
          });
        }
      }
    },
  });
};

export const useAddOrUpdateCelebration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['add-update-celebration'],
    mutationFn: async ({ formValues, post, files }) => {
      const isEdit = !!post;

      const basePath = 'celebrations';
      const path = isEdit ? `${basePath}/${post.id}` : basePath;

      const formData = await preProcessCelebration(formValues, files);

      if (!formData) {
        throw new Error(`Error creating celebration`);
      }

      const options = {
        method: isEdit ? 'PUT' : 'POST',
        body: formData,
      };

      const { ok, data: serverData } = await authorizedServerFetch(
        path,
        options,
      );

      if (!ok || !serverData) {
        throw new Error(`Error creating celebration`);
      }

      return serverData.data;
    },
    onSuccess: (serverData, { post }) => {
      const isEdit = !!post;
      // TODO - Shyam handle reward
      const { post: celebrationPost, reward } = serverData;
      if (isEdit) {
        const { pageIndex, postIndex } = getPostFromCache(post.id, queryClient);
        if (pageIndex >= 0 && postIndex >= 0) {
          queryClient.setQueryData(feedCacheKey, (oldData) => {
            return produce(oldData, (draft) => {
              draft.pages[pageIndex].posts[postIndex] = celebrationPost;
            });
          });
        }
      } else {
        queryClient.setQueryData(feedCacheKey, (oldData) => {
          return produce(oldData, (draft) => {
            draft.pages[0].posts = [celebrationPost, ...draft.pages[0].posts];
          });
        });
      }
    },
  });
};

export const useAddOrUpdateCrowdFund = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['add-crowd-fund'],
    mutationFn: async ({ data, post, images, videos }) => {
      const isEdit = !!post;

      const basePath = 'crowd-funds';
      const path = isEdit ? `${basePath}/${post.sourcePostData.id}` : basePath;

      const formData = await preProcessCrowdFund(data, images, videos, post);

      if (!formData) {
        throw new Error(`Error creating crowd fund`);
      }

      const options = {
        method: isEdit ? 'PUT' : 'POST',
        body: formData,
      };

      const { ok, data: serverData } = await authorizedServerFetch(
        path,
        options,
      );

      if (!ok || !serverData) {
        throw new Error(`Error creating post`);
      }

      return serverData.data;
    },
    onSuccess: (serverData, { post }) => {
      const isEdit = !!post;
      const { post: crowdFundPost } = serverData;
      if (isEdit) {
        const { pageIndex, postIndex } = getPostFromCache(post.id, queryClient);
        if (pageIndex >= 0 && postIndex >= 0) {
          queryClient.setQueryData(feedCacheKey, (oldData) => {
            return produce(oldData, (draft) => {
              draft.pages[pageIndex].posts[postIndex] = crowdFundPost;
            });
          });
        }
      } else {
        const startDate = new Date(crowdFundPost.sourcePostData.startDate);
        if (!isAfter(startDate, endOfDay(new Date()))) {
          queryClient.setQueryData(feedCacheKey, (oldData) => {
            return produce(oldData, (draft) => {
              draft.pages[0].posts = [crowdFundPost, ...draft.pages[0].posts];
            });
          });

          if (crowdFundPost.isFeatured) {
            queryClient.setQueryData(
              ['home-feed-featured-posts'],
              (oldData) => {
                return produce(oldData, (draft) => {
                  draft.unshift(crowdFundPost);
                });
              },
            );
          }
        }
      }
    },
  });
};

export const useFetchPublicFeed = (initialData, fetcher) => {
  return useInfiniteQuery({
    queryKey: feedCacheKey,
    queryFn: async ({ pageParam }) => {
      const data = pageParam ? await fetcher(pageParam) : initialData;

      return {
        posts: data || [],
        hasMore: size(data) > 0 && !data[0]?.renderProps?.isPromotionPost,
        // hasMore: size(data) > 0,
      };
    },
    getNextPageParam: (lastPage) => {
      if (lastPage && !lastPage.hasMore) {
        return undefined;
      }

      const lastPostDate = last(lastPage.posts)?.createdOn;
      return lastPostDate || undefined;
    },
    initialData: () => {
      if (size(initialData) === 0) {
        return undefined;
      }

      return {
        pages: [
          {
            posts: initialData,
            hasMore:
              size(initialData) > 0 &&
              !initialData[0]?.renderProps?.isPromotionPost,
            // hasMore: size(initialData) > 0,
          },
        ],
      };
    },
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnMount: 'always',
  });
};

export const useFetchProfileFeed = (initialData, fetcher) => {
  return useInfiniteQuery({
    queryKey: feedCacheKey,
    queryFn: async ({ pageParam }) => {
      const data = pageParam ? await fetcher(pageParam || 1) : initialData;

      return {
        posts: data || [],
        hasMore: size(data) > 0,
      };
    },
    // getNextPageParam: (lastPage, allPages) => {
    //   return size(allPages) + 1;
    // },
    // initialData: () => {
    //   if (size(initialData) === 0) {
    //     return undefined;
    //   }

    //   return {
    //     pages: [
    //       {
    //         posts: initialData,
    //         hasMore: size(initialData) > 0,
    //       },
    //     ],
    //   };
    // },
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = size(allPages) + 1;
      return lastPage?.hasMore ? nextPage : undefined;
    },
    initialData: () => {
      if (size(initialData) === 0) {
        return undefined;
      }
      return {
        pages: [
          {
            posts: initialData,
            hasMore: true, // Assuming initialData represents the first page and there could be more
          },
        ],
      };
    },
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnMount: 'always',
  });
};

export const useFetchMyNonProfitFeed = (initialData, fetcher) => {
  return useInfiniteQuery({
    queryKey: feedCacheKey,
    queryFn: async ({ pageParam }) => {
      const data = pageParam ? await fetcher(pageParam) : initialData;

      return {
        posts: data || [],
        hasMore: size(data) > 0 && !data[0]?.renderProps?.isPromotionPost,
      };
    },
    getNextPageParam: (lastPage) => {
      if (lastPage && !lastPage.hasMore) {
        return undefined;
      }

      const lastPostDate = last(lastPage.posts)?.createdOn;
      return lastPostDate || undefined;
    },
    initialData: () => {
      if (size(initialData) === 0) {
        return undefined;
      }

      return {
        pages: [
          {
            posts: initialData,
            hasMore:
              size(initialData) > 0 &&
              !initialData[0]?.renderProps?.isPromotionPost,
          },
        ],
      };
    },
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnMount: 'always',
  });
};

export const useFetchUserPhilanthropy = (initialData, fetcher, detailView) => {
  return useInfiniteQuery({
    queryKey: ['my-profile-feed-data'],
    queryFn: async ({ pageParam }) => {
      const data = pageParam ? await fetcher(pageParam) : initialData;

      return {
        posts: data || {},
        hasMore: size(data) > 0,
      };
    },
    getNextPageParam: (lastPage) => {
      if (lastPage && !lastPage.hasMore) {
        return undefined;
      }

      let lastPagePost = last(lastPage.posts[detailView]);
      const lastPostDate =
        lastPagePost?.createdOn ||
        lastPagePost?.createdAt ||
        // lastPagePost?.updatedOn ||
        // lastPagePost?.updatedAt ||
        lastPagePost?.donationDate;
      // return lastPostDate || new Date().toISOString();
      // return lastPostDate || undefined;
      return lastPostDate;
    },
    initialData: () => {
      if (size(initialData) === 0) {
        return undefined;
      }

      return {
        pages: [
          {
            posts: initialData,
            hasMore: size(initialData) > 0,
          },
        ],
      };
    },
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnMount: 'always',
  });
};

export const useFetchFeaturedPosts = (initialData) => {
  return useQuery({
    queryKey: ['home-feed-featured-posts'],
    // queryFn: async () => {
    //   const { data, ok } = await fetchViaProxy(
    //     `/proxy/v2/protected/companies/${companyId}/featured-posts`,
    //     { method: 'GET' },
    //   );

    //   if (!ok) {
    //     throw new Error(`Error fetching featured posts`);
    //   }

    //   return data;
    // },
    initialData: () => {
      return initialData;
    },
    staleTime: Infinity,
    cacheTime: Infinity,
  });
};

export const useUpdateNonProfitFollowers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: 'update-nonprofit-followers',
    mutationFn: async ({ nonProfitId, offset }) => {
      queryClient.setQueryData(feedCacheKey, (oldData) => {
        return produce(oldData, (draft) => {
          draft.pages.forEach((page) => {
            page.posts.forEach((post) => {
              if (post.renderProps.nonProfit?.id === nonProfitId) {
                post.renderProps.nonProfit.numFollowers += offset;
              }
              if (
                post.renderProps.nonProfit?.id === nonProfitId &&
                post.author.type === ProfileTypes.Organization
              ) {
                post.author.numFollowers += offset;
              }
            });
          });
        });
      });
      return true;
    },
  });
};

export const useUpdateUserFollowers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: 'update-user-followers',
    mutationFn: async ({ userId, offset }) => {
      queryClient.setQueryData(feedCacheKey, (oldData) => {
        return produce(oldData, (draft) => {
          draft.pages.forEach((page) => {
            page.posts.forEach((post) => {
              if (
                post.author.id === userId &&
                post.author.type === ProfileTypes.Individual
              ) {
                post.author.numFollowers += offset;
              }
            });
          });
        });
      });
      return true;
    },
  });
};

export const useUpdateFundraiserDonation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: 'update-fundraiser-donation',
    mutationFn: async ({ postId, amount, donor }) => {
      queryClient.setQueryData(feedCacheKey, (oldData) => {
        return produce(oldData, (draft) => {
          draft.pages.forEach((page) => {
            page.posts.forEach((post) => {
              if (post.id === postId) {
                // const donatedAmount =
                //   post.totalDonations || post.renderProps.donations.total;
                const donatedAmount =
                  post.renderProps.donations.total || post.totalDonations;
                post.totalDonations = donatedAmount + amount;
                post.renderProps.donations.total = donatedAmount + amount;
                post.totalDonors += 1;
                post.latestDonors = [
                  ...post.latestDonors,
                  {
                    id: donor.id,
                    firstName: donor.first_name,
                    lastName: donor.last_name,
                    avatar: donor.avatar,
                  },
                ];
              }
            });
          });
        });
      });
      return true;
    },
  });
};

export const useUpdateUserVolunteeringStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: 'update-user-volunteering-status',
    mutationFn: async ({ postId, status }) => {
      queryClient.setQueryData(feedCacheKey, (oldData) => {
        return produce(oldData, (draft) => {
          draft.pages.forEach((page) => {
            page.posts.forEach((post) => {
              if (
                post.id === postId &&
                post?.sourcePostData?.volunteerOpportunity?.userVolunteeringInfo
              ) {
                post.sourcePostData.volunteerOpportunity.userVolunteeringInfo =
                  {
                    ...post.sourcePostData.volunteerOpportunity
                      .userVolunteeringInfo,
                    status: status,
                  };
              }
            });
          });
        });
      });
      return true;
    },
  });
};
